import { FC, useRef, useState, DragEvent, ChangeEvent } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import {
  TradeMatchingFileType,
  useUploadTradeMatchingFile,
} from "../../features/tradeMatcher/dataHooks/tradeMatchingQueries";

type Props = {
  fileType: TradeMatchingFileType | null;
  onUploaded?: (message: string) => void;
};

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

const formatBytes = (bytes: number) => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(0)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const dedupeAndAppend = (existing: File[], incoming: File[]): File[] => {
  const seen = new Set(existing.map((f) => `${f.name}|${f.size}|${f.lastModified}`));
  const result = [...existing];
  for (const file of incoming) {
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push(file);
    }
  }
  return result;
};

export const FileUploader: FC<Props> = ({ fileType, onUploaded }) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [oversized, setOversized] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<
    { name: string; message: string }[]
  >([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useUploadTradeMatchingFile();

  const pickFiles = (files: FileList | File[] | null) => {
    if (!files || files.length === 0) {
      return;
    }
    const incoming = Array.from(files);
    const tooBig = incoming.filter((f) => f.size > MAX_BYTES);
    const ok = incoming.filter((f) => f.size <= MAX_BYTES);

    if (tooBig.length > 0) {
      setOversized(tooBig.map((f) => f.name));
    } else {
      setOversized([]);
    }
    if (ok.length > 0) {
      setSelectedFiles((prev) => dedupeAndAppend(prev, ok));
    }
  };

  const removeFile = (file: File) => {
    setSelectedFiles((prev) => prev.filter((f) => f !== file));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    pickFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    pickFiles(e.target.files);
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0 || fileType === null) {
      return;
    }
    setIsUploading(true);
    setUploadErrors([]);

    const remaining: File[] = [];
    const errors: { name: string; message: string }[] = [];
    let succeeded = 0;

    for (const file of selectedFiles) {
      try {
        await upload.mutateAsync({ file, fileType });
        succeeded += 1;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed";
        errors.push({ name: file.name, message });
        remaining.push(file);
      }
    }

    setSelectedFiles(remaining);
    setUploadErrors(errors);
    setIsUploading(false);

    if (succeeded > 0) {
      const summary =
        errors.length === 0
          ? `${succeeded} file${succeeded === 1 ? "" : "s"} uploaded`
          : `${succeeded} uploaded, ${errors.length} failed`;
      onUploaded?.(summary);
    }
  };

  const totalBytes = selectedFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed",
          borderColor: (t) =>
            dragging
              ? t.palette.primary.main
              : t.palette.mode === "dark"
                ? t.palette.grey[600]
                : t.palette.grey[300],
          borderRadius: 1,
          p: 4,
          mb: 2,
          textAlign: "center",
          bgcolor: (t) =>
            dragging
              ? t.palette.action.hover
              : t.palette.mode === "dark"
                ? "#000"
                : t.palette.grey[50],
          cursor: "pointer",
          transition: "border-color .2s, background .2s",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: (t) => t.palette.action.hover,
          },
        }}
      >
        <UploadFileIcon
          sx={{ fontSize: 36, color: "text.disabled", mb: 1 }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ fontWeight: 600 }}
        >
          Drag &amp; drop files here
        </Typography>
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ display: "block", mb: 1.5 }}
        >
          One or many files — max 50 MB each
        </Typography>
        <Button
          variant="text"
          color="warning"
          size="small"
          sx={{ pointerEvents: "none" }}
        >
          Select files
        </Button>
        <input
          ref={inputRef}
          hidden
          type="file"
          multiple
          onChange={handleInputChange}
        />
      </Box>

      {selectedFiles.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {selectedFiles.map((file) => (
            <Box
              key={`${file.name}|${file.size}|${file.lastModified}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.25,
                border: "1px solid",
                borderColor: "primary.light",
                borderRadius: 1,
                bgcolor: "primary.50",
              }}
            >
              <UploadFileIcon fontSize="small" color="primary" />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {file.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ flexShrink: 0 }}
              >
                {formatBytes(file.size)}
              </Typography>
              <Tooltip title="Remove">
                <IconButton
                  size="small"
                  onClick={() => removeFile(file)}
                  disabled={isUploading}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {selectedFiles.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Typography variant="caption" color="text.disabled">
            {selectedFiles.length} file{selectedFiles.length === 1 ? "" : "s"}{" "}
            selected · {formatBytes(totalBytes)}
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={handleUpload}
            disabled={isUploading || fileType === null}
            startIcon={
              isUploading ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
          >
            {isUploading
              ? "Uploading…"
              : fileType === null
                ? "Select a file type first"
                : `Upload ${selectedFiles.length} file${
                    selectedFiles.length === 1 ? "" : "s"
                  }`}
          </Button>
        </Box>
      )}

      {oversized.length > 0 && (
        <Typography variant="caption" color="error" sx={{ display: "block" }}>
          Skipped (over 50 MB): {oversized.join(", ")}
        </Typography>
      )}
      {uploadErrors.length > 0 && (
        <Box>
          {uploadErrors.map((e) => (
            <Typography
              key={e.name}
              variant="caption"
              color="error"
              sx={{ display: "block" }}
            >
              {e.name}: {e.message}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};
